const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = url;
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
  });

export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Recorta `imageSrc` a la región `pixelCrop` (en píxeles reales de la imagen)
 * y devuelve un Blob.
 *
 * @param imageSrc  data URL / blob URL / URL de la imagen.
 * @param pixelCrop región a recortar, en píxeles naturales de la imagen.
 * @param mimeType  formato de salida. `image/png` (por defecto) conserva
 *                  transparencia — ideal para logos. Usar `image/jpeg` para
 *                  fotos donde el peso importa.
 * @param quality   calidad 0..1 (solo aplica a jpeg/webp).
 */
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: PixelCrop,
  mimeType: string = "image/png",
  quality: number = 0.92
): Promise<Blob> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) throw new Error("No se pudo obtener el contexto del canvas");

  const width = Math.max(1, Math.round(pixelCrop.width));
  const height = Math.max(1, Math.round(pixelCrop.height));

  canvas.width = width;
  canvas.height = height;

  ctx.drawImage(
    image,
    Math.round(pixelCrop.x),
    Math.round(pixelCrop.y),
    width,
    height,
    0,
    0,
    width,
    height
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Error al generar la imagen recortada"));
      },
      mimeType,
      quality
    );
  });
};

export default getCroppedImg;
